import React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const textAreaVariants = cva(
  'flex w-full rounded-xs bg-primary-lighter shadow-sm transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      color: {
        primary:
          '[--input-color:hsla(var(--primary-color))] [--input-focus-color:hsla(var(--primary-color-light))]',
        'primary-light':
          '[--input-color:hsla(var(--primary-color-light))] [--input-focus-color:hsla(var(--primary-color))]',
      },
      variant: {
        default:
          'border-[--input-color] text-[--input-color] placeholder:font-extralight placeholder:text-[--input-color] focus-visible:border-[--input-focus-color]',
      },
      size: {
        xs: 'h-[24px] rounded-md px-[10px] text-sm font-medium',
        sm: 'h-[39px] rounded-md p-2 text-sm font-medium placeholder:font-normal',
        md: 'h-[49px] border-2 px-4 text-sm [&_svg]:size-[28px]',
        lg: 'h-[57px] border px-6 text-md [&_svg]:size-[38px]',
      },
    },
    defaultVariants: {
      color: 'primary-light',
      variant: 'default',
      size: 'md',
    },
  }
);

export type TextAreaProps = Omit<React.ComponentProps<'textarea'>, 'size'> &
  VariantProps<typeof textAreaVariants> & {
    wrapperClassName?: string;
    label?: string;
    errors?: string[];
  };

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ wrapperClassName, className, size, variant, color, label, errors, ...props }, ref) => (
    <label
      className={cn(
        'flex size-full cursor-text flex-col gap-[10px] overflow-scroll',
        wrapperClassName
      )}
    >
      {label && <span className="select-none text-xs text-text-secondary">{label}</span>}
      <textarea
        ref={ref}
        className={textAreaVariants({
          className: cn('h-full overflow-hidden', className),
          size,
          variant,
          color,
        })}
        {...props}
      />
      {errors?.map((error, index) => (
        <p
          key={error}
          role="alert"
          className="select-none overflow-auto text-xs text-danger opacity-0 motion-safe:animate-slide-in"
          style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
        >
          {error}
        </p>
      ))}
    </label>
  )
);

TextArea.displayName = 'TextArea';

export { TextArea };
